import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Input } from '@/components/atoms/Input';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Textarea } from '@/components/atoms/Textarea';
import { useAuthStore } from '@/store/authStore';
import { useScrollLock } from '@/hooks/useScrollLock';

interface AdminComment {
  id: number;
  post_id: number;
  user_id: string;
  author_nickname: string | null;
  content: string;
  is_blinded: boolean;
  blind_reason: string | null;
  is_deleted: boolean;
  like_count: number;
  report_count: number;
  created_at: string;
  post_title: string | null;
  post_board_id: number | null;
  post_board_name: string | null;
  post_board_slug: string | null;
}

const LIMIT = 30;
function fmt(iso: string) {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

export function CommentsListPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const hidden = params.get('hidden') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [draftQ, setDraftQ] = useState(q);
  const me = useAuthStore((s) => s.me);
  const isSuper = me?.adminRole === 'super';
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'comments', { q, hidden, page }],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (q) sp.set('q', q);
      if (hidden) sp.set('hidden', hidden);
      sp.set('page', String(page));
      sp.set('limit', String(LIMIT));
      return get<{ comments: AdminComment[]; total: number }>(`/admin/comments?${sp}`);
    },
  });
  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  const [actionTarget, setActionTarget] = useState<{ type: 'hide' | 'delete'; comment: AdminComment } | null>(
    null,
  );
  const [reason, setReason] = useState('');
  useScrollLock(actionTarget !== null);
  const refetch = () => qc.invalidateQueries({ queryKey: ['admin', 'comments'] });

  const hideMut = useMutation({
    mutationFn: (cid: number) => post(`/admin/comments/${cid}/hide`, { reason }),
    onSuccess: () => {
      setActionTarget(null);
      setReason('');
      refetch();
    },
  });
  const unhideMut = useMutation({
    mutationFn: (cid: number) => post(`/admin/comments/${cid}/unhide`),
    onSuccess: refetch,
  });
  const deleteMut = useMutation({
    mutationFn: (cid: number) => post(`/admin/comments/${cid}/delete`, { reason }),
    onSuccess: () => {
      setActionTarget(null);
      setReason('');
      refetch();
    },
  });

  return (
    <div className="w-full">
      <h1 className="mb-6 text-2xl font-extrabold">댓글 관리</h1>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder="내용 검색"
          value={draftQ}
          onChange={(e) => setDraftQ(e.target.value)}
          onKeyDown={(e) =>
            e.key === 'Enter' &&
            (() => {
              const next = new URLSearchParams(params);
              if (draftQ) next.set('q', draftQ);
              else next.delete('q');
              next.set('page', '1');
              setParams(next);
            })()
          }
          className="max-w-xs"
        />
        <select
          value={hidden}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            if (e.target.value) next.set('hidden', e.target.value);
            else next.delete('hidden');
            next.set('page', '1');
            setParams(next);
          }}
          className="h-9 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-sm"
        >
          <option value="">전체</option>
          <option value="false">정상만</option>
          <option value="true">블라인드만</option>
        </select>
        <div className="ml-auto text-xs text-[var(--color-text-sub)]">총 {data?.total ?? 0}건</div>
      </div>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <ul className="space-y-2">
          {data?.comments.map((c) => (
            <li
              key={c.id}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
            >
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-sub)]">
                <Link
                  to={`/admin/users/${c.user_id}`}
                  className="font-medium text-[var(--color-primary)] hover:underline"
                >
                  {c.author_nickname ?? '-'}
                </Link>
                <span>·</span>
                <span>{fmt(c.created_at)}</span>
                {c.is_deleted && <Badge variant="default">삭제</Badge>}
                {c.is_blinded && <Badge variant="fall">블라인드</Badge>}
                {c.report_count > 0 && <Badge variant="fall">신고 {c.report_count}</Badge>}
              </div>
              {/* 원본 게시물 — 게시판/제목/링크 (새창) */}
              <Link
                to={`/post/${c.post_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-2 flex items-center gap-1.5 rounded-md bg-[var(--color-bg)] px-2 py-1.5 text-xs hover:bg-[var(--color-border)]"
              >
                {c.post_board_name && (
                  <Badge variant="outline">{c.post_board_name}</Badge>
                )}
                <span className="truncate font-medium text-[var(--color-text)]">
                  {c.post_title ?? `(삭제된 게시물 #${c.post_id})`}
                </span>
                <span className="ml-auto shrink-0 text-[var(--color-text-sub)]">
                  #{c.post_id}
                </span>
              </Link>
              <p className="text-sm">{c.content}</p>
              {c.is_blinded && c.blind_reason && (
                <p className="mt-1 text-xs text-[var(--color-text-sub)]">사유: {c.blind_reason}</p>
              )}
              <div className="mt-2 flex justify-end gap-1">
                {c.is_blinded ? (
                  <Button size="sm" variant="ghost" onClick={() => unhideMut.mutate(c.id)}>
                    해제
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setActionTarget({ type: 'hide', comment: c })}
                  >
                    숨김
                  </Button>
                )}
                {isSuper && !c.is_deleted && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => setActionTarget({ type: 'delete', comment: c })}
                  >
                    완전삭제
                  </Button>
                )}
              </div>
            </li>
          ))}
          {data?.comments.length === 0 && (
            <li className="p-8 text-center text-[var(--color-text-sub)]">데이터가 없어요.</li>
          )}
        </ul>
      )}

      {data && data.total > LIMIT && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('page', String(page - 1));
              setParams(next);
            }}
          >
            이전
          </Button>
          <span className="text-sm text-[var(--color-text-sub)]">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('page', String(page + 1));
              setParams(next);
            }}
          >
            다음
          </Button>
        </div>
      )}

      {actionTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setActionTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-6"
          >
            <h3 className="mb-3 text-lg font-bold">
              {actionTarget.type === 'hide' ? '댓글 숨김' : '댓글 완전삭제'}
            </h3>
            {actionTarget.type === 'delete' && (
              <p className="mb-3 rounded-md bg-[var(--color-warn-bg)] p-2 text-xs text-[var(--color-warn)]">
                ⚠️ 완전삭제는 복구 불가. admin_logs 에 백업이 보관됩니다.
              </p>
            )}
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="사유 (필수, 2자 이상)"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm"
            />
            <div className="mt-4 flex gap-2">
              <Button variant="ghost" fullWidth onClick={() => setActionTarget(null)}>
                취소
              </Button>
              <Button
                fullWidth
                variant="danger"
                disabled={reason.trim().length < 2}
                loading={hideMut.isPending || deleteMut.isPending}
                onClick={() =>
                  actionTarget.type === 'hide'
                    ? hideMut.mutate(actionTarget.comment.id)
                    : deleteMut.mutate(actionTarget.comment.id)
                }
              >
                적용
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
